// ABOUTME: The Scope step's editable state: hierarchy, optional branch, condition rows and the read-only filters.
// ABOUTME: Built from a dialog request or a stored scope, and turned back into the BulkChangeScope a run takes.

import { SearchFilter, SearchFilterOperator } from '@src/__generated__/types';

import { BulkChangeScope } from '../types';
import { ConditionRow, fromSearchFilter, toSearchFilter } from './conditionOperators';

/** The search filter of the nodes below `nodeId`: the server's scope subtree, used by the list view link. */
export const ancestorFilter = (nodeId: string): SearchFilter => ({
    field: 'ancestor',
    operator: SearchFilterOperator.Equals,
    value: [nodeId],
});

export type ScopeDraft = {
    hierarchyId: string;
    /** The branch root; absent means the whole hierarchy. */
    rootNodeId?: string;
    /** The label of the branch shown in the picker and the recap. */
    branchLabel?: string;
    conditions: ConditionRow[];
    /** Filters the builder cannot edit (e.g. a linked-by filter of the list): kept as they are. */
    lockedFilters: SearchFilter[];
};

export const EMPTY_SCOPE_DRAFT: ScopeDraft = {
    hierarchyId: '',
    conditions: [],
    lockedFilters: [],
};

/** A hierarchy root as branch means the whole hierarchy. */
const branchOf = (hierarchyId: string, rootNodeId?: string) =>
    rootNodeId && rootNodeId !== hierarchyId ? rootNodeId : undefined;

export function scopeDraftFrom({
    hierarchyId = '',
    rootNodeId,
    filters = [],
    branchLabel,
}: {
    hierarchyId?: string;
    rootNodeId?: string;
    filters?: SearchFilter[];
    branchLabel?: string;
}): ScopeDraft {
    const conditions: ConditionRow[] = [];
    const lockedFilters: SearchFilter[] = [];
    filters
        // The list view always filters on its own hierarchy: the scope says it already.
        .filter(filter => filter.field !== 'hierarchy')
        .forEach(filter => {
            const row = fromSearchFilter(filter);
            if (row) conditions.push(row);
            else lockedFilters.push(filter);
        });
    const branch = branchOf(hierarchyId, rootNodeId);
    return {
        hierarchyId,
        ...(branch ? { rootNodeId: branch, branchLabel } : {}),
        conditions,
        lockedFilters,
    };
}

/** The scope of a draft; null until a hierarchy is chosen. Incomplete rows are left out. */
export function buildScope(draft: ScopeDraft): BulkChangeScope | null {
    if (!draft.hierarchyId) return null;
    const filters = [
        ...draft.lockedFilters,
        ...draft.conditions
            .map(toSearchFilter)
            .filter((filter): filter is SearchFilter => !!filter),
    ];
    return {
        hierarchyId: draft.hierarchyId,
        ...(draft.rootNodeId ? { rootNodeId: draft.rootNodeId } : {}),
        filters,
    };
}

/** Rows the user started but did not finish: they block the Next button. */
export const hasIncompleteConditions = (draft: ScopeDraft) =>
    draft.conditions.some(row => !toSearchFilter(row));
